import React from "react";
import { useTranslation } from "react-i18next";
import mixpanel from "mixpanel-browser";
import {
  Box,
  Button,
  CompassColor,
  H5,
  Image,
  Link,
  P3,
  Stack,
} from "@noom/wax-component-library";

import { trackDatadogError } from "@app/datadog";

import { MixpanelTypes } from "../../common/types";
import errorIcon from "../../image_assets/marketplace_error_icon.svg";

const SUPPORT_EMAIL = "support@noom.com";

/**
 * Default localized fallback UI. Extracted into a function component because
 * `ErrorBoundary` itself must be a class (error boundaries have no hook
 * equivalent) and therefore cannot call `useTranslation` directly.
 */
const DefaultErrorFallback = () => {
  const { t } = useTranslation("common", { useSuspense: false });

  return (
    <Box
      data-cy="error-boundary"
      minH="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      p={6}
    >
      <Stack spacing={6} align="center" maxWidth="360px">
        <Image src={errorIcon} alt="" width="108px" height="108px" />
        <Stack spacing={2} align="center">
          <H5
            fontWeight="400"
            fontSize="28px"
            lineHeight="34px"
            textAlign="center"
          >
            {t("errorBoundary.title")}
          </H5>
          <P3 textAlign="center">{t("errorBoundary.body")}</P3>
        </Stack>
        <Button
          size="md"
          borderRadius="56px"
          bg={CompassColor.lagoon}
          color={CompassColor.white}
          onClick={() => window.location.reload()}
          data-cy="error-boundary-retry"
        >
          {t("errorBoundary.retry")}
        </Button>
        <Link href={`mailto:${SUPPORT_EMAIL}`} color={CompassColor.denim}>
          {t("errorBoundary.contactSupport")}
        </Link>
      </Stack>
    </Box>
  );
};

type ErrorBoundaryProps = {
  children: React.ReactNode;
  /**
   * Custom fallback to render instead of the default UI. Receives the caught
   * error and a `reset` callback that clears the boundary so the tree can be
   * re-rendered without a full page reload.
   */
  fallback?: (props: {
    error: Error;
    reset: () => void;
  }) => React.ReactNode;
  /**
   * Called in addition to the built-in Mixpanel and Datadog reporting — use it
   * to forward the error to other sinks or run recovery side effects.
   */
  onError?: (error: Error, info: React.ErrorInfo) => void;
};

type ErrorBoundaryState = {
  error: Error | null;
};

/**
 * Top-level React error boundary. Catches render/lifecycle errors from its
 * subtree, reports them to Mixpanel (product funnel) and Datadog RUM
 * (operational alerting), and shows a retryable fallback instead of an
 * unmounted white screen.
 *
 * Error boundaries must be class components — there is no hook equivalent — so
 * this cannot consume `useLogger`; it logs via the globally-initialized
 * `mixpanel` instance (see `src/index.tsx`).
 */
export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Surface in the console for local debugging / source maps.
    console.error("Uncaught error caught by ErrorBoundary:", error, info);

    try {
      mixpanel.track(MixpanelTypes.AppError, {
        error_name: error.name,
        error_message: error.message,
        component_stack: info.componentStack,
      });
    } catch {
      // Never let error reporting throw out of the boundary.
    }

    try {
      // RUM already collects unhandled errors, but one caught by a boundary is
      // reported as handled — so send it explicitly, with the component stack
      // RUM cannot infer on its own.
      trackDatadogError(error, {
        source: "error_boundary",
        componentStack: info.componentStack,
      });
    } catch {
      // Never let error reporting throw out of the boundary.
    }

    this.props.onError?.(error, info);
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;

    if (error) {
      if (this.props.fallback) {
        return this.props.fallback({ error, reset: this.reset });
      }

      return <DefaultErrorFallback />;
    }

    return this.props.children;
  }
}
